// SMART on FHIR providers for "connect your hospital record".
//
// Sandbox only. Both servers below hold invented patients, and isEhrEnabled()
// keeps the entry point off production — a real connection needs the token
// exchange to move server-side first (a refresh token is a long-lived key to
// somebody's chart, and a browser is the wrong place to keep one).

/** Where the hospital sends the patient back. Registered with the provider. */
export const EHR_REDIRECT_URI = `${window.location.origin}/ehr/callback`;

/**
 * Asked for at authorization. Epic grants what the app registration lists
 * rather than what is requested here, so the two have to agree: these match
 * the APIs ticked on fhir.epic.com.
 */
const PATIENT_SCOPES = [
  "openid",
  "fhirUser",
  "patient/Patient.read",
  "patient/AllergyIntolerance.read",
  "patient/Condition.read",
  "patient/MedicationRequest.read",
];

// Vite replaces `process.env.REACT_APP_*` at build time, but ONLY for names
// that exist in the loaded .env file — an absent one is left as a bare
// `process` reference that throws in the browser. The typeof guard means a
// missing client ID simply hides Epic instead of breaking the page.
const readEnv = (value) => (typeof process === "undefined" ? "" : value || "");

export const EHR_PROVIDERS = {
  // Needs no registration at all, so the flow can be exercised end to end
  // before any client ID exists.
  "smart-health-it": {
    id: "smart-health-it",
    label: "Demo hospital (SMART Health IT)",
    fhirBaseUrl: "https://launch.smarthealthit.org/v/r4/fhir",
    clientId: "healthdesk-poc",
    scopes: PATIENT_SCOPES,
  },
  // Epic's developer sandbox. The client ID is the NON-PRODUCTION one from the
  // app's page on fhir.epic.com; it travels in the authorize URL, so it is an
  // identifier rather than a secret.
  "epic-sandbox": {
    id: "epic-sandbox",
    label: "Epic MyChart (sandbox)",
    fhirBaseUrl: "https://fhir.epic.com/interconnect-fhir-oauth/api/FHIR/R4",
    clientId: readEnv(
      typeof process === "undefined" ? "" : process.env.REACT_APP_EPIC_SANDBOX_CLIENT_ID
    ),
    scopes: PATIENT_SCOPES,
  },
};

export const getProvider = (id) => EHR_PROVIDERS[id] || null;

/** Providers that can actually be used — Epic only once its client ID is set. */
export const availableProviders = () =>
  Object.values(EHR_PROVIDERS).filter((p) => p.clientId);

/** Production is deliberately excluded; see the note at the top. */
export const isEhrEnabled = () =>
  readEnv(
    typeof process === "undefined" ? "" : process.env.REACT_APP_FIREBASE_PROJECT_ID
  ) !== "hlthdsk" && availableProviders().length > 0;
